<script setup>
/* global axios */
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import ConversationAPI from 'dashboard/api/inbox/conversation';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const isAiEnabled = computed(() => {
  const attrs = currentChat.value?.custom_attributes || {};
  return attrs.ai_enabled !== false;
});

const aiToggleLabel = computed(() =>
  isAiEnabled.value ? 'MODO IA ATIVADO' : 'MODO IA DESATIVADO'
);

const aiToggleIcon = computed(() =>
  isAiEnabled.value ? 'i-lucide-bot' : 'i-lucide-bot-off'
);

const aiToggleColor = computed(() =>
  isAiEnabled.value ? 'blue' : 'ruby'
);

const toggleAi = async () => {
  const newValue = !isAiEnabled.value;
  try {
    await ConversationAPI.updateCustomAttributes({
      conversationId: currentChat.value.id,
      customAttributes: { ai_enabled: newValue },
    });
    store.dispatch('updateCustomAttributes', {
      conversationId: currentChat.value.id,
      customAttributes: { ai_enabled: newValue },
    });
    useAlert(newValue ? 'IA ativada para esta conversa' : 'IA desativada para esta conversa');
  } catch (error) {
    useAlert('Erro ao alterar modo IA');
  }
};

/* ---- META WPP: continuar o mesmo cliente na caixa de WhatsApp -------------
 * Só aparece quando o admin ligou META WPP na aba "Configuração do Site" da
 * caixa E escolheu a caixa de WhatsApp destino. A leitura vem do próprio
 * objeto da caixa que já está no store (additional_attributes é exposto na
 * API de caixas) — sem chamada extra.
 * O clique NÃO envia mensagem: fora da janela de 24h a Meta só aceita template
 * aprovado, e quem escolhe é o atendente na aba de Templates do Chatwoot.
 */
const route = useRoute();
const router = useRouter();
const abrindoWpp = ref(false);

const inboxDaConversa = computed(
  () => store.getters['inboxes/getInbox'](currentChat.value?.inbox_id) || {}
);

const telefoneDoCliente = computed(() =>
  String(currentChat.value?.meta?.sender?.phone_number || '').replace(/\D/g, '')
);

const mostrarBotaoWpp = computed(() => {
  const aa = inboxDaConversa.value?.additional_attributes || {};
  return (
    aa.mazam_web_meta_wpp === true &&
    !!aa.mazam_web_meta_wpp_inbox_id &&
    !!telefoneDoCliente.value
  );
});

const abrirNoWhatsapp = async () => {
  if (abrindoWpp.value) return;
  abrindoWpp.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${route.params.accountId}/mazam_web/abrir_no_whatsapp`,
      {
        conversation_id: currentChat.value.id,
        inbox_id: currentChat.value.inbox_id,
      }
    );
    useAlert(
      data.criada
        ? `Conversa aberta em ${data.inbox_name}`
        : `Este cliente já tinha conversa em ${data.inbox_name}`
    );
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: data.conversation_id },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível abrir no WhatsApp'
    );
  } finally {
    abrindoWpp.value = false;
  }
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ButtonV4
      v-if="mostrarBotaoWpp"
      v-tooltip="'Continuar este cliente na caixa de WhatsApp'"
      label="WHATSAPP"
      size="sm"
      color="teal"
      icon="i-ri-whatsapp-line"
      :is-loading="abrindoWpp"
      @click="abrirNoWhatsapp"
    />
    <ButtonV4
      :label="aiToggleLabel"
      size="sm"
      :color="aiToggleColor"
      :icon="aiToggleIcon"
      @click="toggleAi"
    />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
